import { useState, useEffect } from "react";
import PlatformBrand from "../components/PlatformBrand";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sun, Moon, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loginPath, setLoginPath] = useState("/login");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get(`/users/invite/${token}`);
        setInviteInfo(data);
        setLoginPath(data.login_path || (data.portal_slug ? `/login/${data.portal_slug}` : "/login"));
        if (data.tenant_name) document.title = `Join ${data.tenant_name} — Fixlr`;
      } catch (e) {
        setError(e.response?.data?.detail || "Invalid or expired invitation link");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const tenantName = inviteInfo?.tenant_name || inviteInfo?.company_name || "";
  const tenantLogo = inviteInfo?.tenant_logo_url || inviteInfo?.logo_public_path || "";
  const tenantLogoSrc = tenantLogo ? (tenantLogo.startsWith("http") ? tenantLogo : `${BACKEND}${tenantLogo}`) : "";
  const accent = inviteInfo?.primary_color || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirmPassword) { toast.error("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      const { data } = await API.post(`/users/invite/${token}/accept`, { password });
      if (data?.login_path) setLoginPath(data.login_path);
      setSuccess(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to activate account");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="accept-invite-page">
      {/* Left panel */}
      <div className={`hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center ${isDark ? "login-panel-dark" : "bg-primary login-pattern"}`}>
        <div className="relative z-10 px-16 text-white">
          <PlatformBrand variant="login-hero" className="mb-8" />
          <h1 className="text-4xl sm:text-5xl font-bold font-[Chivo] tracking-tight mb-4 leading-tight">
            {tenantName ? <>Welcome to<br />{tenantName}</> : <>Welcome to<br />the Team</>}
          </h1>
          <p className={`text-base leading-relaxed max-w-md ${isDark ? "text-slate-300" : "opacity-70"}`}>
            {tenantName
              ? `Set up your account to start working with ${tenantName} on jobs, suppliers and works orders.`
              : "Set up your account to start managing projects, suppliers, and works orders on the Fixlr."}
          </p>
        </div>
        {isDark && (
          <>
            <div className="login-hex-grid" />
            <div className="login-glow login-glow-orange" />
            <div className="login-glow login-glow-blue" />
          </>
        )}
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2" data-testid="invite-brand">
              {tenantLogoSrc ? (
                <img src={tenantLogoSrc} alt={tenantName} className="h-8 max-w-[160px] object-contain" />
              ) : (
                <img src="/fixlr-icon.png" alt="Fixlr" className="h-8 w-8 object-contain" />
              )}
              <span className="font-bold text-lg font-[Chivo]">{tenantName || "Fixlr"}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
          </div>

          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          )}

          {error && !loading && (
            <div className="text-center" data-testid="invite-error">
              <div className="mb-4 p-3 rounded-md bg-destructive/10 text-destructive text-sm">{error}</div>
              <Button variant="outline" onClick={() => navigate("/login")}>Go to Sign In</Button>
            </div>
          )}

          {success && (
            <div className="text-center" data-testid="invite-success">
              <div className="h-16 w-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-emerald-600" weight="fill" />
              </div>
              <h2 className="text-xl font-bold font-[Chivo] mb-2">Account Activated</h2>
              <p className="text-sm text-muted-foreground mb-2">Your account is ready. You can now sign in with your email and password.</p>
              {loginPath !== "/login" && (
                <p className="text-xs text-muted-foreground mb-6 font-mono" data-testid="invite-door-hint">{window.location.host}{loginPath}</p>
              )}
              <Button className="w-full" style={accent ? { backgroundColor: accent } : undefined} onClick={() => window.location.assign(loginPath)} data-testid="go-to-login-btn">
                {tenantName ? `Sign in to ${tenantName}` : "Sign In"}
              </Button>
            </div>
          )}

          {inviteInfo && !success && !error && (
            <>
              <h2 className="text-2xl font-bold font-[Chivo] tracking-tight mb-1">Set Up Your Account</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Hi <strong>{inviteInfo.name}</strong>, create a password to activate your account.
              </p>

              <div className="mb-6 p-3 rounded-lg bg-muted/50 border border-border">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">Email:</span>
                  <span className="font-medium">{inviteInfo.email}</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Minimum 8 characters" required minLength={8} data-testid="invite-password" />
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input id="confirmPassword" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password" required minLength={8} data-testid="invite-confirm-password" />
                </div>
                <Button type="submit" className="w-full" disabled={submitting} style={accent ? { backgroundColor: accent } : undefined} data-testid="activate-account-btn">
                  {submitting ? "Activating..." : "Activate Account"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
